import { fromParamsToString, fromStringToParams, isRelative } from './helpers'

export class Url {
	readonly query: Record<string, string> = {}
	readonly hash: Record<string, string> = {}
	private readonly url: URL

	constructor(url = window.location.href) {
		if (isRelative(url)) {
			url = window.location.origin + url
		}

		this.url = new URL(url)

		if (this.url.href.includes('?')) {
			this.query = Object.freeze(fromStringToParams(this.url.search))
		}

		if (this.url.href.includes('#')) {
			this.hash = Object.freeze(fromStringToParams(this.url.hash))
		}
	}

	get search() {
		return this.url.search
	}

	includes(pathname: string) {
		return new RegExp(`${pathname}([?#]|$)`).test(this.pathname)
	}

	get hashString() {
		return fromParamsToString(this.hash)
	}

	get hostname() {
		return this.url.hostname
	}

	get origin(): string {
		return this.url.origin
	}

	get pathname() {
		return this.url.pathname
	}

	get queryString() {
		return fromParamsToString(this.query)
	}

	toString() {
		return this.origin + this.pathname + this.queryString + this.hashString
	}
}

interface UrlBuilderParams {
	url?: Url
	noEmptyValue?: boolean
}

export class UrlBuilder {
	private readonly query: Record<string, string> = {}
	private readonly hash: Record<string, string> = {}
	private origin: string = ''
	private pathname: string = ''
	private readonly noEmptyValue: boolean = false

	constructor(params?: UrlBuilderParams) {
		if (params) {
			const { url, noEmptyValue = false } = params
			this.noEmptyValue = noEmptyValue
			if (url) {
				this.query = { ...url.query }
				this.hash = { ...url.hash }
				this.pathname = url.pathname
				this.origin = url.origin
			}
		}
	}

	setOrigin(origin: string) {
		this.origin = origin
		return this
	}

	removeOrigin() {
		this.origin = ''
		return this
	}

	setPathname(pathname: string) {
		this.pathname = pathname
		return this
	}

	removePathname(pathname: string) {
		this.pathname = this.pathname.replace(pathname, '')
		return this
	}

	addPathname(pathname: string) {
		this.pathname = this.pathname + pathname
		return this
	}

	addPathnameFromStart(pathname: string) {
		this.pathname = pathname + this.pathname
		return this
	}

	addQuery(name: string, value: string) {
		if (this.noEmptyValue && !value) {
			return this
		}
		this.query[name] = value
		return this
	}

	addHash(name: string, value: string) {
		this.hash[name] = value
		return this
	}

	removeQuery(name: string) {
		delete this.query[name]
		return this
	}

	removeQueryAll(): this {
		const query = this.query

		for (let queryName in query) {
			delete query[queryName]
		}
		return this
	}

	removeHash(name: string) {
		delete this.hash[name]
		return this
	}

	removeHashAll() {
		const hash = this.hash

		for (let queryName in hash) {
			if (hash.hasOwnProperty(queryName)) {
				delete hash[queryName]
			}
		}
		return this
	}

	toString() {
		return this.getOrigin() + this.getPathname() + this.getQuery() + this.getHash()
	}

	build() {
		return new Url(this.toString())
	}

	private getQuery() {
		return fromParamsToString(this.query)
	}

	private getHash() {
		return fromParamsToString(this.hash)
	}

	private getOrigin() {
		return this.origin
	}

	private getPathname() {
		return this.pathname
	}
}
